import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import styles from './ErrorBoundary.module.css';

/**
 * Top-level error boundary for the admin shell.
 *
 * React 18 unmounts the entire root subtree when a component
 * render throws AND no ancestor error boundary catches it,
 * leaving the user staring at a blank `<div id="root"></div>`.
 * That's exactly what bit during the /access user-row-click
 * diagnosis — the throw was real, the visible state was nothing.
 *
 * This boundary wraps the AppShell's children render so any
 * sync render exception in a page component caught here
 * produces a visible recovery UI (error name + message + a
 * "Reload" button + a "Back to dashboard" exit). The console
 * still gets the full stack trace via componentDidCatch so the
 * operator can dig into the cause; the boundary just guarantees
 * the screen never goes blank from an uncaught throw.
 *
 * Scope
 * ─────
 * Wraps page content only — NOT the chrome (sidebar, topbar).
 * If a page component throws, the chrome stays visible so the
 * operator can navigate away to a working page. If the chrome
 * itself throws (very rare; chrome state is minimal and tested
 * across every page nav), the boundary won't catch it — that's
 * the trade-off for keeping nav recovery available when a leaf
 * page errors.
 *
 * Reset behavior
 * ──────────────
 * The "Reload" CTA forces a full window.location.reload() rather
 * than trying to clear the boundary's state and re-render. The
 * thrown error is often the second-order symptom of stale
 * application state (cache mismatch, stale React Query result,
 * pre-token-refresh request); a hard reload is the most reliable
 * recovery. The "Back to dashboard" CTA navigates via
 * window.location.href (also full reload) for the same reason —
 * pushState into a still-broken in-memory React tree would just
 * re-throw.
 *
 * Reset on route change
 * ─────────────────────
 * `resetKey` prop — when the wrapper sees a different value (the
 * AppShell passes the current path), the boundary resets its
 * internal error state so the user navigating away from the
 * broken page lands on a fresh render attempt. Without this, the
 * error UI would persist past navigation.
 */

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Changing this value resets the boundary so a route change
   *  re-attempts render rather than persisting the error UI. */
  resetKey?: string | number;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface the full diagnostic to the console so the
    // operator can dig in. componentDidCatch fires AFTER
    // the React error overlay (which Vite injects in dev),
    // so this log is additive — it stays in the page even
    // after the overlay is dismissed. Production builds don't
    // get the overlay so this is the primary trace surface
    // there.
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught a render exception:', {
      error: { name: error.name, message: error.message, stack: error.stack },
      componentStack: info.componentStack,
    });
  }

  componentDidUpdate(prev: ErrorBoundaryProps): void {
    // Reset when the resetKey changes — typical pattern is
    // the AppShell passing the current route path, so the
    // boundary forgets the previous page's error when the
    // operator navigates away.
    if (
      this.state.error
      && prev.resetKey !== undefined
      && prev.resetKey !== this.props.resetKey
    ) {
      this.setState({ error: null });
    }
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleHome = (): void => {
    // Full nav rather than pushState — the in-memory React
    // tree may still be in a broken state until reload.
    window.location.href = '/dashboard';
  };

  render(): ReactNode {
    if (this.state.error) {
      const { error } = this.state;
      return (
        <div className={styles.boundary} role="alert">
          <div className={styles.icon}>
            <AlertTriangle size={28} />
          </div>
          <h1 className={styles.title}>Something went wrong</h1>
          <p className={styles.subtitle}>
            This page hit an unexpected error. The rest of the app
            still works. Try reloading or go back to the dashboard.
          </p>
          <details className={styles.details}>
            <summary className={styles.detailsSummary}>
              Diagnostic info
            </summary>
            <p className={styles.detailRow}>
              <span className={styles.detailLabel}>Error:</span>
              <span className={styles.detailValue}>{error.name}</span>
            </p>
            <p className={styles.detailRow}>
              <span className={styles.detailLabel}>Message:</span>
              <span className={styles.detailValue}>{error.message}</span>
            </p>
            {error.stack && (
              <pre className={styles.stack}>{error.stack}</pre>
            )}
          </details>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={this.handleReload}
            >
              <RefreshCw size={14} /> Reload page
            </button>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={this.handleHome}
            >
              Back to dashboard
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
